/**
 * Deal pipeline (kanban), grouped by whatever the active saved view says.
 *
 * Deliberately no drag-and-drop: native HTML5 DnD is fiddly and has poor touch
 * support, and dnd-kit would be a new dependency. Each card carries a "move to"
 * select instead — keyboard-accessible, works on touch, costs nothing. Swap in
 * dragging later if the team asks; the mutation it would call is already here.
 *
 * Moving a card *sets the group-by field* to the target column's value. That
 * generalises the old "move to stage" without special-casing: group by owner and
 * the same control reassigns the deal.
 *
 * The whole board arrives in one request (window function server-side), and
 * `useOptimistic` moves the card before the round trip completes.
 */

import { useOptimistic, useTransition } from 'react'
import { Link, useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { Deal, DealBoard } from '@erp/contracts'
import type { FieldRegistry, SavedView } from '@erp/contracts'
import { api } from '../api'
import { filtersParam } from '../view-state'
import { ViewBar, handleViewIntent } from '../components/view-bar'
import { Badge, Card, PageHeader, Select } from '../ui'
import { useUser } from '../status-context'
import { formatDate, formatMoney } from '../format'

interface BoardData {
  board: DealBoard
  registry: FieldRegistry
  views: SavedView[]
}

/**
 * Group-by comes from the URL, which selecting a saved view rewrites — so the
 * board and the view stay in step without either holding the other's state.
 */
export async function loader({ request }: LoaderFunctionArgs): Promise<BoardData> {
  const url = new URL(request.url)
  const [board, registry, views] = await Promise.all([
    api.deals.board({
      groupBy: url.searchParams.get('groupBy') ?? 'statusId',
      filters: filtersParam(url.searchParams),
      state: 'open',
    }),
    api.registry('deal'),
    api.views.forEntity('deal'),
  ])
  return { board, registry, views }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  const result = await handleViewIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

interface Move {
  dealId: string
  toKey: string
}

export default function DealsBoard() {
  const { board, registry, views } = useLoaderData() as BoardData
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const [isPending, startTransition] = useTransition()

  const [optimistic, applyMove] = useOptimistic(board, (current: DealBoard, move: Move) => ({
    ...current,
    columns: current.columns.map((column) => {
      const without = column.rows.filter((deal) => deal.id !== move.dealId)
      if (column.key !== move.toKey) return { ...column, rows: without }

      const moved = current.columns.flatMap((c) => c.rows).find((d) => d.id === move.dealId)
      return moved ? { ...column, rows: [moved, ...without] } : { ...column, rows: without }
    }),
  }))

  function move(deal: Deal, toKey: string) {
    const target = board.columns.find((column) => column.key === toKey)

    // A losing stage needs a reason, which the board cannot collect inline.
    if (target?.outcome === 'lost') {
      navigate(`/crm/deals/${deal.id}?intent=lost`)
      return
    }

    startTransition(async () => {
      applyMove({ dealId: deal.id, toKey })
      // Grouping by status routes through changeStatus so the outcome trigger
      // and its constraint apply; every other axis is a plain field update.
      if (board.groupBy === 'statusId') {
        await api.deals.changeStatus(deal.id, { statusId: toKey, lostReasonId: null })
      } else {
        await api.deals.update(deal.id, { [board.groupBy]: toKey === '' ? null : toKey })
      }
      revalidator.revalidate()
    })
  }

  function regroup(field: string) {
    const next = new URLSearchParams(searchParams)
    next.set('groupBy', field)
    navigate({ search: next.toString() })
  }

  const groupOptions = registry.groupableFields.map((field) => ({
    value: field,
    label: registry.fields.find((f) => f.field === field)?.label ?? field,
  }))

  return (
    <>
      <PageHeader
        title="Pipeline"
        actions={
          <>
            <Select name="groupBy" value={board.groupBy} options={groupOptions} onChange={regroup} />
            <Link
              className="ax-btn"
              to={{ pathname: '/crm/deals/list', search: searchParams.toString() }}
            >
              List
            </Link>
            <a className="ax-btn ax-btn-primary" href="/crm/deals/new">
              Create
            </a>
          </>
        }
      />

      <ViewBar entity="deal" registry={registry} views={views} />

      <div className="ax-board" aria-busy={isPending}>
        {optimistic.columns.map((column) => (
          <section key={column.key} className="ax-board-column">
            <header className="ax-board-column-header">
              <Badge color={column.color ?? 'gray'}>{column.label}</Badge>
              <span className="ax-board-count">{column.totalCount}</span>
              {column.totalAmount !== null ? (
                <span className="ax-board-total">
                  {formatMoney(column.totalAmount, column.totalCurrency)}
                </span>
              ) : null}
            </header>

            <div className="ax-board-cards">
              {column.rows.map((deal) => (
                <Card key={deal.id}>
                  <a className="ax-card-title" href={`/crm/deals/${deal.id}`}>
                    {deal.amount !== null ? formatMoney(deal.amount, deal.currency) : 'No amount'}
                  </a>
                  <p className="ax-card-meta">
                    {deal.expectedCloseDate
                      ? `Closes ${formatDate(deal.expectedCloseDate)}`
                      : 'No close date'}
                  </p>
                  {/* Owner on the card too, since grouping by stage hides it. */}
                  <p className="ax-card-meta">
                    <OwnerName ownerId={deal.ownerId} />
                  </p>
                  <Select
                    name={`move-${deal.id}`}
                    value={column.key}
                    options={board.columns.map((c) => ({ value: c.key, label: c.label }))}
                    onChange={(toKey) => move(deal, toKey)}
                  />
                </Card>
              ))}

              {column.rows.length < column.totalCount ? (
                <p className="ax-board-more">{column.totalCount - column.rows.length} more</p>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </>
  )
}

function OwnerName({ ownerId }: { ownerId: string | null }) {
  const owner = useUser(ownerId)
  if (ownerId === null) return <span className="ax-text-muted">Unassigned</span>
  return <span>{owner?.name ?? 'Unknown'}</span>
}
